import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Radio from '@mui/material/Radio';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import Container from '@mui/system/Container';
import Uploader from 'front/Uploader';
import useOnScreen from 'front/useOnScreen';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import classifyPoint from 'robust-point-in-polygon';
import { MAX_UPLOAD_FILE_SIZE_IN_MB } from '../../common/api';
import { hasErrors } from '../../common/enums';
import { mapsPlaceToPlaceData } from '../../common/enums/maps';
import { contactSlice } from '../../common/store/contactSlice';
import { recaptchaSlice } from '../../common/store/recaptchaSlice';
import { AppState, appStore, franchiseSelector, territorySelector, zoneSelector } from '../../common/store/store';
import { apiJson } from '../api';
import PlaceAutocomplete from '../libraries/CLIENT_PlaceAutocomplete';
import { PhoneInput } from '../utils/PhoneInput';


const WarnMessage = () => {
    return <Alert icon={<GppMaybeIcon fontSize="inherit" />} severity='warning'>Hmmmm. Il semble y avoir un problème avec votre appareil. Il est impossible d'utiliser ce formulaire de soumission. Veuillez transmettre toute demande d'information ou soumission de prix par courriel à info@lespieuxprotech.com.</Alert>;
};

const callback = (...args: any) => {
    console.log('callback', args);
};

interface EmplacementItem { value: string, franchiseId: number | null; }
const sortEmplacement = (a: EmplacementItem, b: EmplacementItem) => { return a.value.localeCompare(b.value); };

export const Contact = (props: { test?: Boolean; }) => {


    const sendCliqueSurTelephone = () => {

        (window as any)['gtag']('event', 'conversion_event_contact', {
            'event_callback': callback,
            'event_timeout': 2000
        });

    };

    const ref = React.useRef<HTMLDivElement>(null);
    const isVisible = useOnScreen(ref);

    React.useEffect(() => {
        appStore.dispatch(contactSlice.actions.setVisible(isVisible as any)); // must cast any because of server compiler (don't know useOnScreen type)
    }, [isVisible]);

    const franchise = useSelector((state: AppState) => franchiseSelector.selectById(state, state.device.currFranchise));
    const zones = useSelector((state: AppState) => zoneSelector.selectAll(state));

    const submission = useSelector((state: AppState) => state.contact.submission);
    const choixDebutTravaux = useSelector((state: AppState) => state.contact.choixDebutTravaux);

    const territories = useSelector((state: AppState) => territorySelector.selectAll(state));


    const [error, setError] = React.useState<string[]>([]);
    const [sending, setSending] = React.useState(false);

    const [errors, setErrors] = React.useState<ResponseError[] | null>(null);

    const [emplacementOptions, setEmplacementOptions] = React.useState<EmplacementItem[]>([]);

    React.useEffect(() => {
        const municipalites: string[] = [];
        let arr: { franchiseId: number | null; value: string; }[] = territories.map(p => { return { franchiseId: p.franchiseId, value: p.name }; });
        territories.forEach(p => {
            arr = arr.concat(
                p.regions.reduce((acc: { franchiseId: number | null; value: string; }[], q) => {
                    if (municipalites.indexOf(q) === -1) {
                        municipalites.push(q);
                        acc.push({
                            franchiseId: p.franchiseId,
                            value: q
                        });
                    }
                    return acc;
                }, []));
        });


        setEmplacementOptions([...arr.sort(sortEmplacement)]);
    }, [territories]);




    const canUploadFile = useSelector((state: AppState) => state.recaptcha.canUploadFile);
    //const canUseContactForm = useSelector((state: AppState) => state.recaptcha.canUseContactForm);
    const [canUseContactForm, _setIt] = React.useState(true);

    React.useEffect(() => {
        appStore.dispatch(recaptchaSlice.actions.setActive(true));
        appStore.dispatch(contactSlice.actions.setActive(true));
        return () => {
            appStore.dispatch(recaptchaSlice.actions.setActive(false));
            appStore.dispatch(contactSlice.actions.setActive(false));
        };
    }, []);

    const navigate = useNavigate();
    const sendMessage = async () => {
        setSending(true);
        setErrors(null);
        setError([]);
        try {
            const result: ResponseError[] | string = await apiJson('/submission/send', 'post', submission);

            setSending(false);
            if (hasErrors(result)) {
                setErrors(result);
                setError([`Une ou plusieurs erreurs se sont glissés, en rouge, dans le formulaire.`]);
            } else if (typeof result == 'string') {

                appStore.dispatch(contactSlice.actions.clear());
                navigate('/messageSent/' + result);
            } else {
                setError([`Des problèmes techniques sont survenues. Impossible de transmettre votre demande.`]);
            }
        } catch (err: any) {
            setSending(false);
            setError([`Des problèmes techniques sont survenues. Impossible de transmettre votre demande.`]);
        }
    };

    const [selectedPlace, setSelectedPlace] = React.useState<google.maps.places.PlaceResult | null>(null);

    React.useEffect(() => {
        if (selectedPlace) {

            console.log('selectedPlace', selectedPlace);

            selectedPlace?.geometry?.location?.lat;
            const data: any = {
                formatted_address: selectedPlace.formatted_address,
                geometry: {
                    location: selectedPlace.geometry?.location?.toJSON(),
                    viewport: selectedPlace.geometry?.viewport?.toJSON()
                },
                name: selectedPlace.name
            };

            appStore.dispatch(contactSlice.actions.updateSubmission({
                emplacement: mapsPlaceToPlaceData(selectedPlace)
            }));
            const lat = selectedPlace?.geometry?.location?.lat();
            const lng = selectedPlace?.geometry?.location?.lng();
            if (lat && lng) {
                const zone = zones.find(zone => {
                    return classifyPoint(zone.paths.map(q => [q.lat, q.lng]), [lat, lng]) == -1;
                });
                if (zone && zone.franchiseId) {
                    appStore.dispatch(contactSlice.actions.updateSubmission({
                        franchiseId: zone.franchiseId
                    }));
                    return;
                }
            }
        }
        appStore.dispatch(contactSlice.actions.updateSubmission({
            franchiseId: 1
        }));
    }, [selectedPlace]);


    return <><Container sx={{ pt: 10 }}>

        <Box id='soumission' sx={{ alignItems: 'center', textAlign: 'center' }}>

            <Typography variant='h2' style={{ textAlign: 'center' }}>Parlez-nous de votre projet</Typography>
            <Typography variant='h4'>Nous vous transmettrons une soumission dans les plus brefs délais !</Typography>
            {sending && <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={sending}
            >
                <CircularProgress size={4} color="inherit" />
            </Backdrop>}
            <Dialog
                open={error.length !== 0}
                onClose={() => { setError([]); }}
            >
                <DialogTitle>Attention</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {error.map(p => <p>{p}</p>)}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => { setError([]); }}>OK</Button>
                </DialogActions>
            </Dialog>
            {canUseContactForm === false ? <WarnMessage /> : null}
            <div ref={ref}>
                <form id="contactForm">
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <Typography sx={{ textAlign: 'left' }}>Quand souhaitez-vous entreprendre votre projet ? <sup>*</sup></Typography>
                        <List sx={{ bgcolor: 'background.paper' }}>
                            {choixDebutTravaux.map((choix, index) => {
                                const labelId = 'choix-debut-travaux-' + index;
                                return <ListItem
                                    disablePadding
                                    key={index} >
                                    <ListItemButton
                                        dense
                                        onClick={(event) => {
                                            appStore.dispatch(contactSlice.actions.updateSubmission({
                                                debutTravaux: choix
                                            }));
                                        }}>
                                        <ListItemIcon color={errors?.find(p => p.param == 'debutTravaux') ? 'error' : undefined}>
                                            <Radio
                                                color={errors?.find(p => p.param == 'debutTravaux') ? 'error' : undefined}
                                                value={choix}
                                                edge='start'
                                                checked={choix === submission.debutTravaux}
                                                tabIndex={-1}
                                                disableRipple
                                                inputProps={{ 'aria-labelledby': labelId }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText id={labelId} primary={choix} />
                                    </ListItemButton>
                                </ListItem>;
                            })}
                            <ListItem
                                disablePadding
                                key={'autre'} >
                                <ListItemButton
                                    dense>
                                    <ListItemIcon>
                                        <Radio
                                            edge='start'
                                            checked={choixDebutTravaux.indexOf(submission.debutTravaux ?? '') == -1 && !!submission.debutTravaux}
                                            tabIndex={-1}
                                            disableRipple
                                            inputProps={{ 'aria-labelledby': 'autre' }}
                                        />
                                    </ListItemIcon>
                                    <ListItemText
                                        id={'autre'}
                                        primary={<TextField
                                            placeholder='Autre / Date précise'
                                            onChange={event => {
                                                appStore.dispatch(contactSlice.actions.updateSubmission({
                                                    debutTravaux: event.target.value
                                                }));
                                            }}
                                            value={choixDebutTravaux.indexOf(submission.debutTravaux ?? '') == -1 ? submission.debutTravaux ?? '' : ''}
                                            error={!!errors?.find(p => p.param == 'debutTravaux')}
                                            helperText={errors?.find(p => p.param == 'debutTravaux')?.msg ?? undefined}
                                        />} />
                                </ListItemButton>
                            </ListItem>
                        </List>
                    </Box>
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <Typography sx={{ textAlign: 'left', mb: 2 }}>Emplacement des travaux ? <sup>*</sup></Typography>
                        <PlaceAutocomplete
                            name={Math.random() + ''}
                            id={Math.random() + ''}
                            placeholder='Adresse, municipalité ou ville'
                            autoComplete='off'
                            onChange={(event) => {
                                console.log('place value', event.target.value);
                                appStore.dispatch(contactSlice.actions.updateSubmission({
                                    emplacement: event.target.value
                                }));
                            }}
                            sx={{ bgcolor: 'background.paper' }}
                            fullWidth
                            onPlaceSelect={setSelectedPlace}
                            error={!!errors?.find(p => p.param == 'emplacement')}
                            helperText={errors?.find(p => p.param == 'emplacement')?.msg ?? undefined}
                        />
                    </Box>
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <Typography variant='subtitle1' sx={{ pb: 1, textAlign: 'left' }}>Vous pourrez joindre des documents (images/pdf) plus bas dans ce formulaire.</Typography>
                        <TextField
                            sx={{ bgcolor: 'background.paper' }}
                            fullWidth
                            required
                            multiline
                            rows={5}
                            disabled={canUseContactForm === false}
                            label='Description de votre projet'
                            value={submission.description}
                            onChange={(event) => {
                                appStore.dispatch(contactSlice.actions.updateSubmission({ description: event.target.value }));
                            }}
                            name='description'
                            error={!!errors?.find(p => p.param == 'description')}
                            helperText={errors?.find(p => p.param == 'description')?.msg ?? undefined}
                        />
                    </Box>
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <TextField
                            sx={{ bgcolor: 'background.paper' }}
                            fullWidth
                            label="Votre nom"
                            required
                            disabled={canUseContactForm === false}
                            value={submission.name}
                            onChange={(event) => {
                                appStore.dispatch(contactSlice.actions.updateSubmission({ name: event.target.value }));
                            }}
                            name='name'
                            error={!!errors?.find(p => p.param == 'name')}
                            helperText={errors?.find(p => p.param == 'name')?.msg ?? undefined}
                        />
                    </Box>
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <PhoneInput
                            fullWidth
                            required
                            sx={{ bgcolor: 'background.paper' }}
                            label={'Numéro de téléphone'}
                            value={submission.tel}
                            disabled={canUseContactForm === false}
                            onChange={(value: string | null) => {
                                appStore.dispatch(contactSlice.actions.updateSubmission({ tel: value ?? '' }));
                            }}
                            name="tel"
                            id="cellphone"
                            error={!!errors?.find(p => p.param == 'tel')}
                            helperText={errors?.find(p => p.param == 'tel')?.msg ?? undefined}
                        />
                    </Box>
                    <Box
                        sx={{ margin: '2em 0' }}>
                        <TextField
                            sx={{ bgcolor: 'background.paper' }}
                            fullWidth
                            label='Adresse courriel'
                            type='email'
                            value={submission.email}
                            disabled={canUseContactForm === false}
                            onChange={(event) => {
                                appStore.dispatch(contactSlice.actions.updateSubmission({ email: event.target.value }));
                            }}
                            name='email'
                            error={!!errors?.find(p => p.param == 'email')}
                            helperText={errors?.find(p => p.param == 'email')?.msg ?? undefined}
                        />
                    </Box>

                    {canUploadFile ? <Box
                        sx={{ margin: '2em 0' }}>
                        <Typography variant='h2'>Dépôt de documents et plans</Typography>
                        <Typography>Merci de bien vouloir fournir tout les documents pertinents au sujet de votre projet; tel que plans, devis, certificat de localisation, images, photos, croquis, etc. <strong>Seulement les image (JPEG / PNG) et documents pdf sont autorisés</strong>. Poid maximum des fichiers : {MAX_UPLOAD_FILE_SIZE_IN_MB} Mo.</Typography>
                        <Uploader multiple={true} onChange={(files: number[]) => {
                            appStore.dispatch(contactSlice.actions.updateSubmission({ files: files }));
                        }} />
                    </Box> : null}
                    <Box
                        sx={{ margin: '2em 0' }}>

                        {canUseContactForm === false ? <WarnMessage /> : null}
                        <Button
                            disabled={canUseContactForm !== true}
                            onClick={sendMessage}
                            color="primary"
                            variant="contained"
                        >Transmettre</Button>
                    </Box>
                    <Typography sx={{ fontSize: '0.7em' }}>Ce site est protégé par reCAPTCHA et la <a href="https://policies.google.com/privacy">politique de confidentialité</a> et les <a href="https://policies.google.com/terms">conditions d'utilisation</a> de Google s'appliquent.                </Typography>
                    <Typography sx={{ minHeight: 100, m: 3 }}>La présente demande sera transmise à <Link onClick={sendCliqueSurTelephone} to={"mailto:" + (franchise?.email ?? 'info@lespieuxprotech.com')}>{franchise?.email ?? 'info@lespieuxprotech.com'}</Link></Typography>
                </form>
            </div>
        </Box>
    </Container></>;
};
